import React, { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card, Grid, TableContainer } from '@mui/material'

import { useRoleCheck } from '../../features/account/hooks/roleHooks'
import { RoleEnum } from '../../features/account/types/RoleEnum'
import { RevenueAndDownloadEstimates } from '../../features/estimates/services/RevenueAndDownloadEstimates'
import { GameCardContent } from '../../features/game/components/GameCard/GameCard'
import { GamePowerscore } from '../../features/game/components/GamePowerscore/GamePowerscore'
import { Game } from '../../features/game/types/Game'
import { CellValue } from '../../features/soft-launch/components/CellValue/CellValue'
import { CurrencyIndicator } from '../../features/soft-launch/components/CurrencyIndicator/CurrencyIndicator'
import { TrendIndicator } from '../../features/soft-launch/components/TrendIndicator/TrendIndicator'
import { TableSortValues } from '../../types/TableSortValues'
import { AnalystOverviewDialog } from '../AnalystOverviewDialog/AnalystOverviewDialog'
import { GRTable, GRTableColumn, SortOrder } from '../GRTable/GRTable'
import { LockedData } from '../LockedData/LockedData'
import NotAvailableIcon from '../NotAvailableIcon/NotAvailableIcon'
import { ReviewIndicator } from '../ReviewIndicator/ReviewIndicator'
import styles from './GamesTableWith30DRevDlEstimates.module.scss'

type GamesTableWith30DRevDlEstimatesProps = {
  games: Game[]
  marketIso: string
  isLoading: boolean
  hasUndefinedSubgenre?: boolean
  // The "/games/explorer/market" endpoint does not return the `archive` field, so
  // non-analyzed games would otherwise be wrongly tagged as "Removed". Callers
  // fed by that endpoint (e.g. the Other {subgenre} games modal) pass this to
  // match Market Explorer, which never shows the label. (GR-2943)
  hideRemovedLabel?: boolean
}

/**
 * Component for displaying Games Table with 30 Days Revenue & Downloads Estimates
 */
export const GamesTableWith30DRevDlEstimates: React.FC<GamesTableWith30DRevDlEstimatesProps> = memo(
  ({ games, marketIso, isLoading, hasUndefinedSubgenre, hideRemovedLabel }) => {
    const { t } = useTranslation()
    const [analystOverviewDialogGame, setAnalystOverviewDialogGame] = useState<Game | null>(null)
    const estimatesUnlocked = useRoleCheck(RoleEnum.revenue_estimates)

    const tableColumns: GRTableColumn<Game, undefined>[] = [
      {
        labelAccessor: t('common:game'),
        columns: [
          {
            labelAccessor: t('common:name'),
            accessor: ({ row: game }) => {
              return <GameCardContent game={game} mainMarketIso={marketIso} hidePowerscore hideRemovedLabel={hideRemovedLabel} />
            },
            sortable: true,
            sortAccessor: 'name',
            cellProps: { align: 'left' },
            headerCellProps: { align: 'left', sx: { minWidth: 200 } },
            defaultSortOrder: SortOrder.ASC,
          },
          {
            labelAccessor: t('common:game_power_score'),
            accessor: ({ row: game }) => (
              <Grid container alignItems="center" justifyContent="center">
                <Grid item width={'40px'}>
                  {game.reviewId && game.reviewPublished && <ReviewIndicator onClick={() => setAnalystOverviewDialogGame(game)} />}
                </Grid>
                <Grid item width={'40px'} marginRight={'40px'}>
                  <GamePowerscore value={game.gpsPerMarket[marketIso]} />
                </Grid>
              </Grid>
            ),
            sortable: true,
            sortAccessor: ({ row: game }) => game.gpsPerMarket[marketIso] || TableSortValues.AlwaysLast,
            cellProps: { align: 'center' },
            headerCellProps: { sx: { width: 80 } },
            defaultSortOrder: SortOrder.DESC,
          },
        ],
      },
      {
        labelAccessor: `${t('common:30_days_revenue')} (${t('common:apple_ios')})`,
        columns: [
          {
            labelAccessor: t('common:value_text'),
            accessor: ({ row }) => (
              <>
                {estimatesUnlocked && (
                  <CurrencyIndicator estimate={new RevenueAndDownloadEstimates(row, marketIso).revenue30Day} maxFractionDigits={2} shorten={true} />
                )}
                {!estimatesUnlocked && <LockedData />}
              </>
            ),
            sortable: true,
            sortOrder: SortOrder.DESC,
            sortAccessor: ({ row }) => new RevenueAndDownloadEstimates(row, marketIso).revenue30Day.value || TableSortValues.AlwaysLast,
          },
          {
            labelAccessor: t('common:trend'),
            accessor: ({ row }) => (
              <>
                {estimatesUnlocked && <TrendIndicator estimate={new RevenueAndDownloadEstimates(row, marketIso).revenueTrend30Day} />}
                {!estimatesUnlocked && <LockedData />}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row }) => new RevenueAndDownloadEstimates(row, marketIso).revenueTrend30Day.value || TableSortValues.AlwaysLast,
          },
        ],
      },
      {
        labelAccessor: `${t('common:30_days_downloads')} (${t('common:apple_ios')})`,
        columns: [
          {
            labelAccessor: t('common:value_text'),
            accessor: ({ row }) => (
              <>
                {estimatesUnlocked && (
                  <CellValue value={new RevenueAndDownloadEstimates(row, marketIso).downloads30Day.value} emphasized>
                    {new RevenueAndDownloadEstimates(row, marketIso).downloads30Day.format({ maxFractionDigits: 2, shorten: true })}
                  </CellValue>
                )}
                {!estimatesUnlocked && <LockedData />}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row }) => new RevenueAndDownloadEstimates(row, marketIso).downloads30Day.value || TableSortValues.AlwaysLast,
          },
          {
            labelAccessor: t('common:trend'),
            accessor: ({ row }) => (
              <>
                {estimatesUnlocked && <TrendIndicator estimate={new RevenueAndDownloadEstimates(row, marketIso).downloadsTrend30Day} />}
                {!estimatesUnlocked && <LockedData />}
              </>
            ),
            sortable: true,
            sortAccessor: ({ row }) => new RevenueAndDownloadEstimates(row, marketIso).downloadsTrend30Day.value || TableSortValues.AlwaysLast,
          },
        ],
      },
      {
        labelAccessor: `${t('common:30_days_revenue_download_ratio')} (${t('common:apple_ios')})`,
        columns: [
          {
            labelAccessor: t('common:value_text'),
            accessor: ({ row }) => {
              if (!estimatesUnlocked) return <LockedData />
              const ratio = new RevenueAndDownloadEstimates(row, marketIso).revenueAndDownloadsRatio30Day
              if (ratio.value > 0) {
                return <CurrencyIndicator estimate={ratio} maxFractionDigits={2} threshold={0} />
              }
              return <NotAvailableIcon tooltipContent={t('common:revenue_download_ratio_not_accurate')} />
            },
            sortable: true,
            sortAccessor: ({ row }) => new RevenueAndDownloadEstimates(row, marketIso).revenueAndDownloadsRatio30Day.value || TableSortValues.AlwaysLast,
          },
          {
            labelAccessor: t('common:trend'),
            accessor: ({ row }) => {
              if (!estimatesUnlocked) return <LockedData />
              // When the RV/DL value cell shows the N/A icon (ratio not available), the
              // trend cell would otherwise render a misleading "0%" TrendIndicator. Mirror
              // the value cell's N/A state instead so both cells stay consistent. (GR-2407)
              const estimates = new RevenueAndDownloadEstimates(row, marketIso)
              if (!(estimates.revenueAndDownloadsRatio30Day.value > 0)) {
                return <NotAvailableIcon tooltipContent={t('common:revenue_download_ratio_not_accurate')} />
              }
              return <TrendIndicator estimate={estimates.revenueAndDownloadsRatioTrend30Day} />
            },
            sortable: true,
            sortAccessor: ({ row }) => new RevenueAndDownloadEstimates(row, marketIso).revenueAndDownloadsRatioTrend30Day.value || TableSortValues.AlwaysLast,
          },
        ],
      },
    ]

    const [columns, setColumns] = useState<GRTableColumn<Game, undefined>[]>(tableColumns)
    const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<Game, undefined>[]) => {
      setColumns(updatedColumns)
    }, [])

    return (
      <>
        <TableContainer component={Card} className={styles.container}>
          <GRTable
            rows={games}
            columns={columns}
            rowIdKey="id"
            striped
            hoverable
            onColumnsUpdated={handleColumnsUpdate}
            noRowsLabel={hasUndefinedSubgenre ? t('market-explorer:no_games_available_with_undefined_subgenre') : t('market-explorer:no_games_available')}
            isLoading={isLoading}
          />
        </TableContainer>
        <AnalystOverviewDialog
          reviewId={analystOverviewDialogGame?.reviewId}
          gameName={analystOverviewDialogGame?.resolvedName}
          open={!!analystOverviewDialogGame}
          onClose={() => setAnalystOverviewDialogGame(null)}
        />
      </>
    )
  }
)
